<script setup>
import { APP_URL } from '@/lib/site';
import Icon from '@/components/ui/Icon.vue';

defineProps({
  steps: { type: Array, required: true },
  index: { type: Number, required: true },
  celebrating: { type: Boolean, default: false },
  minimised: { type: Boolean, default: false }
});

const emit = defineEmits(['next', 'skip', 'restart', 'toggle']);
</script>

<template>
  <button v-if="minimised" type="button" class="pill" @click="emit('toggle')">
    <span v-if="index < steps.length">Step {{ index + 1 }} of {{ steps.length }}</span>
    <span v-else>Tour finished</span>
    <Icon name="plus" :size="13" />
  </button>

  <div v-else class="callout" :class="{ celebrating }" role="status" aria-live="polite">
    <div class="top">
      <span class="count">
        <template v-if="index < steps.length">Step {{ index + 1 }} of {{ steps.length }}</template>
        <template v-else>Tour finished</template>
      </span>
      <button type="button" class="hide" aria-label="Minimise tour" @click="emit('toggle')"><Icon name="minus" :size="14" /></button>
    </div>

    <div class="copy">
      <Transition name="swap" mode="out-in">
        <div v-if="index < steps.length" :key="index">
          <p class="title">{{ steps[index].title }}</p>
          <p class="hint">{{ steps[index].hint }}</p>
        </div>
        <div v-else key="done">
          <p class="title">That's the real Minni Map.</p>
          <p class="hint">Your own roadmaps live in your browser, ready the moment you open the app.</p>
        </div>
      </Transition>
    </div>

    <div class="progress" aria-hidden="true">
      <span v-for="(s, i) in steps" :key="s.goal" :class="{ done: i < index || (i === index && celebrating) }" />
    </div>

    <div class="actions">
      <template v-if="index < steps.length">
        <button type="button" class="text" @click="emit('skip')">Skip tour</button>
        <button type="button" class="next" @click="emit('next')">
          <Icon v-if="celebrating" name="check" :size="14" />
          {{ celebrating ? 'Done' : 'Next' }}
        </button>
      </template>
      <template v-else>
        <button type="button" class="text" @click="emit('restart')">Start again</button>
        <a class="next" :href="APP_URL" target="_blank" rel="noopener">Open Minni Map</a>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.callout {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'top top'
    'text actions'
    'progress progress';
  column-gap: 24px;
  width: 520px;
  padding: 14px 18px 14px;
  border-radius: 14px;
  background: var(--iris);
  color: #fff;
  box-shadow: 0 20px 40px -12px rgba(43, 44, 160, 0.55), 0 2px 6px rgba(9, 3, 21, 0.18);
  transition: background-color 0.25s;

  &.celebrating {
    background: #3d9f7b;
  }
}

.top {
  grid-area: top;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.count {
  font-size: 12px;
  font-weight: 500;
  opacity: 0.8;
}

.hide {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  margin-right: -6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #fff;
  opacity: 0.75;
  cursor: pointer;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.14);
  }
}

.title {
  margin-top: 2px;
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.hint {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.82;
}

.copy {
  grid-area: text;
  min-width: 0;
}

.progress {
  grid-area: progress;
  display: flex;
  gap: 4px;
  margin-top: 12px;

  span {
    flex: 1;
    height: 3px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.28);
    transition: background-color 0.3s;

    &.done {
      background: #fff;
    }
  }
}

.actions {
  grid-area: actions;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 10px;
}

.text {
  padding: 0;
  border: 0;
  background: none;
  color: #fff;
  font-size: 12px;
  opacity: 0.75;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
}

.next {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: #fff;
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    background: #f1f1ff;
  }
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 14px;
  border: 0;
  border-radius: 999px;
  background: var(--iris);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 10px 24px -8px rgba(43, 44, 160, 0.55);
  cursor: pointer;
}

.swap-enter-active,
.swap-leave-active {
  transition: opacity 0.18s, transform 0.18s;
}

.swap-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.swap-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
